import React, { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import Tippy from "@tippyjs/react";
import { Link } from "react-router-dom";
import "tippy.js/dist/tippy.css";
import "./App.css";

/***************************************************************************
 * CUSTOM HOOK: useDarkMode
 ***************************************************************************/
function useDarkMode() {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);
  return [darkMode, setDarkMode];
}

/***************************************************************************
 * HELPER FUNCTIONS FOR GROUPING (YEAR / MONTH / WEEK)
 ***************************************************************************/
function getGroupKey(dateStr, scale) {
  const d = new Date(dateStr);
  if (scale === "year") {
    return d.getFullYear().toString();
  } else if (scale === "month") {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  } else if (scale === "week") {
    // Use Sunday as the week’s start
    const sunday = new Date(d);
    sunday.setDate(d.getDate() - d.getDay());
    return (
      sunday.getFullYear() +
      "-" +
      String(sunday.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(sunday.getDate()).padStart(2, "0")
    );
  }
}

function generateGroups(scale, minDate, maxDate) {
  let groups = [];
  let current = new Date(minDate);
  if (scale === "month") {
    current.setDate(1);
  } else if (scale === "week") {
    current.setDate(current.getDate() - current.getDay());
  }
  while (current <= maxDate) {
    let key, label;
    if (scale === "year") {
      key = current.getFullYear().toString();
      label = key;
      current = new Date(current.getFullYear() + 1, 0, 1);
    } else if (scale === "month") {
      key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}`;
      label = key;
      current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    } else if (scale === "week") {
      key =
        current.getFullYear() +
        "-" +
        String(current.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(current.getDate()).padStart(2, "0");
      label = key;
      current = new Date(current.getTime() + 7 * 24 * 3600 * 1000);
    }
    groups.push({ key, label });
  }
  return groups;
}

/***************************************************************************
 * GRID TIMELINE (Tailwind & React)
 * Now uses Tippy for tooltips.
 ***************************************************************************/
function GridTimeline({ customDates, personName, scale, darkMode, setDarkMode, onReset }) {
  const circleSize = "4rem"; // matches w-16
  const gapSize = "0.5rem";  // matches gap-2
  const maxColumns = 10;
  const maxWidth = `calc(${maxColumns} * ${circleSize} + ${maxColumns - 1} * ${gapSize})`;

  // Determine min and max dates from events (or use today if none)
  const dates = customDates.map((e) => new Date(e.date));
  const minDate = dates.length > 0 ? new Date(Math.min(...dates.map((d) => d.getTime()))) : new Date();
  const maxDate = dates.length > 0 ? new Date(Math.max(...dates.map((d) => d.getTime()))) : new Date();

  const groups = generateGroups(scale, minDate, maxDate);

  // Group events by key
  const eventsByGroup = {};
  customDates.forEach((event) => {
    const key = getGroupKey(event.date, scale);
    if (!eventsByGroup[key]) eventsByGroup[key] = [];
    eventsByGroup[key].push(event);
  });

  // Render emojis:
  const renderEmojis = (events) => {
    if (events.length === 0) return null;
    if (events.length === 1) {
      return <span className="text-2xl">{events[0].emoji}</span>;
    } else {
      return (
        <div className="relative">
          <div className="flex items-center justify-center absolute -right-4 top-1/2 transform -translate-x-1/2 bg-gray-100 border border-1 border-gray-600 text-xs text-gray-600 uppercase font-semibold w-4 h-4 rounded-full shadow whitespace-nowrap">
            {events.length}
          </div>
          <span className="text-2xl">{events[0].emoji}</span>
        </div>
      );
    }
  };

  return (
    <div className="w-screen p-6 relative">
      {/* Header */}
      <div
        style={{
          gridTemplateColumns: `repeat(auto-fit, minmax(${circleSize}, 1fr))`,
          maxWidth: maxWidth,
          margin: "0 auto",
        }}
      >
        <div className="flex flex-row items-center justify-between mb-4">
          <div className="flex flex-row items-center gap-x-4 text-[#222]/40 dark:text-gray-500">
            {/* Reset timeline on click */}
            <div onClick={onReset} className="font-ubuntu text-xl transition cursor-pointer">
              vizzy.life
            </div>
            <button
              className="text-[#222]/40 dark:text-gray-500 transition"
              onClick={() => setDarkMode(!darkMode)}
            >
              {!darkMode ? (
                <div className="flex flex-row items-center gap-x-4">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
                    <path
                      fillRule="evenodd"
                      d="M7.455 2.004a.75.75 0 0 1 .26.77 7 7 0 0 0 9.958 7.967.75.75 0 0 1 1.067.853A8.5 8.5 0 1 1 6.647 1.921a.75.75 0 0 1 .808.083Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              ) : (
                <div className="flex flex-row items-center gap-x-4">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
                    <path d="M10 2a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 10 2ZM10 15a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 10 15ZM10 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM15.657 5.404a.75.75 0 1 0-1.06-1.06l-1.061 1.06a.75.75 0 0 0 1.06 1.06l1.06-1.06ZM6.464 14.596a.75.75 0 1 0-1.06-1.06l-1.06 1.06a.75.75 0 0 0 1.06 1.06l1.06-1.06ZM18 10a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 18 10ZM5 10a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 5 10ZM14.596 15.657a.75.75 0 0 0 1.06-1.06l-1.06-1.061a.75.75 0 1 0-1.06 1.06l1.06 1.06ZM5.404 6.464a.75.75 0 0 0 1.06-1.06l-1.06-1.06a.75.75 0 1 0-1.061 1.06l1.06 1.06Z" />
                  </svg>
                </div>
              )}
            </button>
            <a href="https://ko-fi.com/joseph_dev" className="font-ubuntu text-xl text-[#222]/40 dark:text-gray-500 transition">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
                <path d="m9.653 16.915-.005-.003-.019-.01a20.759 20.759 0 0 1-1.162-.682 22.045 22.045 0 0 1-2.582-1.9C4.045 12.733 2 10.352 2 7.5a4.5 4.5 0 0 1 8-2.828A4.5 4.5 0 0 1 18 7.5c0 2.852-2.044 5.233-3.885 6.82a22.049 22.049 0 0 1-3.744 2.582l-.019.01-.005.003h-.002a.739.739 0 0 1-.69.001l-.002-.001Z" />
              </svg>
            </a>
          </div>
          <Link to="/create" className="font-ubuntu text-xl text-[#222]/40 dark:text-gray-500 transition">
            create my own
          </Link>
        </div>
        <div className="font-bebas text-2xl mb-0 text-[#222]/90 dark:text-white transition">
          THE LIFE OF...
        </div>
        <div className="font-bebas text-6xl mb-2 text-[#222]/90 dark:text-white transition">
          {personName}
        </div>
      </div>

      {/* Timeline grid */}
      <div
        className="grid gap-2 justify-items-center"
        style={{
          gridTemplateColumns: `repeat(auto-fit, minmax(${circleSize}, 1fr))`,
          maxWidth: maxWidth,
          margin: "0 auto",
        }}
      >
        {groups.map((group, index) => {
          const groupEvents = eventsByGroup[group.key] || [];
          const computedOpacity = groupEvents.length > 0 ? Math.min(1, 0.6 + groupEvents.length * 0.08) : 0.4;
          const currentYear = new Date().getFullYear();
          const isFutureYear = scale === "year" && parseInt(group.key) > currentYear;
          const circleClasses =
            isFutureYear
              ? "bg-transparent text-gray-700 border border-gray-700"
              : groupEvents.length > 0
                ? "bg-[#ff6800] text-white border-[#ff6800]"
                : "bg-transparent text-gray-700 border border-[#222]/40 dark:border-gray-500 dark:text-gray-500 transition";

          // Build the tooltip content
          const tooltipContent = (
            <div>
              <div className="font-bold">{group.label}</div>
              <ul className="mt-1">
                {groupEvents.map((e, i) => (
                  <li key={i} className="text-xs">
                    {e.emoji} {e.description}
                  </li>
                ))}
              </ul>
            </div>
          );

          const circle = (
            <div
              key={group.key}
              className={`w-16 h-16 rounded-full flex flex-col items-center justify-center transition transform duration-300 cursor-pointer hover:scale-110 opacity-0 animate-pop ${circleClasses}`}
              style={{ animationDelay: `${index * 20}ms` }}
            >
              <div className="flex items-center justify-center text-lg">
                {groupEvents.length > 0 ? renderEmojis(groupEvents) : <span></span>}
              </div>
              <div className="mt-1 text-xs">{group.label}</div>
            </div>
          );

          // If there are events, wrap with Tippy so the tooltip shows
          return groupEvents.length > 0 ? (
            <Tippy key={group.key} content={tooltipContent} placement="bottom" arrow={true} interactive={true}>
              {circle}
            </Tippy>
          ) : (
            circle
          );
        })}
      </div>
    </div>
  );
}

/***************************************************************************
 * MAIN COMPONENT: Vizzy
 * - Uses timelineData (null by default) to decide whether to display the form or timeline.
 ***************************************************************************/
export default function Vizzy() {
  const [darkMode, setDarkMode] = useDarkMode();
  const [timelineData, setTimelineData] = useState(null); // Holds { personName, events } after API call
  const [personName, setPersonName] = useState("");
  const [scale, setScale] = useState("year");
  const [loading, setLoading] = useState(false);

  // Firebase Functions
  const functions = getFunctions();
  const generateTimelineCallable = httpsCallable(functions, "generateTimeline");

  // Create a helper function for searching by name.
  const searchForName = async (name) => {
    setPersonName(name);
    setLoading(true);
    try {
      // Call the cloud function with the person's name
      const result = await generateTimelineCallable({ name });
      if (result.data) {
        // Expecting { personName, response } where response is an array of events
        setTimelineData({
          personName: result.data.personName,
          events: result.data.response,
        });
      } else {
        console.error("No timeline data returned:", result.data);
      }
    } catch (error) {
      console.error("Error generating timeline:", error);
    }
    setLoading(false);
  };

  // Form submit handler – simply calls searchForName with the current personName
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!personName) return;
    await searchForName(personName);
  };

  // Typewriter effect for the input placeholder
  const activitySentences = [
    "Steve Jobs",
    "Elon Musk",
    "Donald Trump",
    "Queen Elizabeth II",
    "Vladimir Putin",
  ];
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [activityText, setActivityText] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  useEffect(() => {
    const currentActivity = activitySentences[currentActivityIndex];
    if (activityText === currentActivity && !isCompleted) {
      setTimeout(() => {
        setIsCompleted(true);
      }, 50);
      setTimeout(() => {
        setActivityText("");
        setIsCompleted(false);
      }, 3000);
      return;
    }
    if (isCompleted) return;
    const intervalId = setInterval(() => {
      setActivityText((prev) => currentActivity.slice(0, prev.length + 1));
      if (activityText === "") {
        setCurrentActivityIndex((prevIndex) => (prevIndex + 1) % activitySentences.length);
      }
    }, 30);
    return () => clearInterval(intervalId);
  }, [currentActivityIndex, activityText, isCompleted]);

  // Trending suggestions – clicking one will populate the search bar and auto-search.
  const trendingNames = ["Steve Jobs", "Elon Musk", "Queen Elizabeth II"];

  return (
    <div className={`flex flex-col items-center ${timelineData === null ? 'justify-center' : ''} min-h-[100dvh] mx-auto bg-[#dad8d2]/70 dark:bg-[#1f1f1f] transition`}>
      {timelineData === null ? (
        <div className="w-full max-w-[600px] p-4">
          <div className="flex flex-row items-center justify-between mb-8">
            <div className="flex flex-row items-center gap-x-4 text-[#222]/40 dark:text-gray-500">
              <div onClick={() => setTimelineData(null)} className="font-ubuntu text-xl cursor-pointer">
                vizzy.life
              </div>
              <button
                className="text-[#222]/40 dark:text-gray-500 transition"
                onClick={() => setDarkMode(!darkMode)}
              >
                {!darkMode ? (
                  <div className="flex flex-row items-center gap-x-4">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
                      <path
                        fillRule="evenodd"
                        d="M7.455 2.004a.75.75 0 0 1 .26.77 7 7 0 0 0 9.958 7.967.75.75 0 0 1 1.067.853A8.5 8.5 0 1 1 6.647 1.921a.75.75 0 0 1 .808.083Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                ) : (
                  <div className="flex flex-row items-center gap-x-4">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
                      <path d="M10 2a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 10 2ZM10 15a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 10 15ZM10 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM15.657 5.404a.75.75 0 1 0-1.06-1.06l-1.061 1.06a.75.75 0 0 0 1.06 1.06l1.06-1.06ZM6.464 14.596a.75.75 0 1 0-1.06-1.06l-1.06 1.06a.75.75 0 0 0 1.06 1.06l1.06-1.06ZM18 10a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 18 10ZM5 10a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 5 10ZM14.596 15.657a.75.75 0 0 0 1.06-1.06l-1.06-1.061a.75.75 0 1 0-1.06 1.06l1.06 1.06ZM5.404 6.464a.75.75 0 0 0 1.06-1.06l-1.06-1.06a.75.75 0 1 0-1.061 1.06l1.06 1.06Z" />
                    </svg>
                  </div>
                )}
              </button>
              <a href="https://ko-fi.com/joseph_dev" className="font-ubuntu text-xl text-[#222]/40 dark:text-gray-500 transition">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
                  <path d="m9.653 16.915-.005-.003-.019-.01a20.759 20.759 0 0 1-1.162-.682 22.045 22.045 0 0 1-2.582-1.9C4.045 12.733 2 10.352 2 7.5a4.5 4.5 0 0 1 8-2.828A4.5 4.5 0 0 1 18 7.5c0 2.852-2.044 5.233-3.885 6.82a22.049 22.049 0 0 1-3.744 2.582l-.019.01-.005.003h-.002a.739.739 0 0 1-.69.001l-.002-.001Z" />
                </svg>
              </a>
            </div>
            <Link to="/create" className="font-ubuntu text-xl text-[#222]/40 dark:text-gray-500 transition">
              create my own
            </Link>
          </div>

          <div className="block mb-2 text-lg font-ubuntu text-[#222]/90 dark:text-white/70 transition">
            Enter a name:
          </div>
          <form onSubmit={handleSubmit} className="flex flex-row gap-x-2 w-full">
            <input
              type="text"
              className="w-full p-2 rounded-md font-ubuntu focus:outline-none focus:ring-0 disabled:bg-white"
              value={personName}
              onChange={(e) => setPersonName(e.target.value)}
              placeholder={activityText}
              disabled={loading}
            />
            <button
              type="submit"
              className="px-4 py-2 bg-[#ff6800] text-white rounded-md font-ubuntu"
              disabled={loading}
            >
              {loading ? (
                <div className={`flex items-center justify-center w-5 h-5`}>
                  <div className={`w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin`}></div>
                </div>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
                  <path
                    fillRule="evenodd"
                    d="M4.606 12.97a.75.75 0 0 1-.134 1.051 2.494 2.494 0 0 0-.93 2.437 2.494 2.494 0 0 0 2.437-.93.75.75 0 1 1 1.186.918 3.995 3.995 0 0 1-4.482 1.332.75.75 0 0 1-.461-.461 3.994 3.994 0 0 1 1.332-4.482.75.75 0 0 1 1.052.134Z"
                    clipRule="evenodd"
                  />
                  <path
                    fillRule="evenodd"
                    d="M5.752 12A13.07 13.07 0 0 0 8 14.248v4.002c0 .414.336.75.75.75a5 5 0 0 0 4.797-6.414 12.984 12.984 0 0 0 5.45-10.848.75.75 0 0 0-.735-.735 12.984 12.984 0 0 0-10.849 5.45A5 5 0 0 0 1 11.25c.001.414.337.75.751.75h4.002ZM13 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          </form>

          {/* Trending tiles */}
          <div className="flex gap-x-2 mt-2 overflow-x-auto w-full whitespace-nowrap no-scrollbar">
            <div className="bg-white/70 dark:bg-white/10 px-2 py-1 rounded-md text-sm font-ubuntu text-[#222]/70 dark:text-white/70">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
                <path
                  fillRule="evenodd"
                  d="M12.577 4.878a.75.75 0 0 1 .919-.53l4.78 1.281a.75.75 0 0 1 .531.919l-1.281 4.78a.75.75 0 0 1-1.449-.387l.81-3.022a19.407 19.407 0 0 0-5.594 5.203.75.75 0 0 1-1.139.093L7 10.06l-4.72 4.72a.75.75 0 0 1-1.06-1.061l5.25-5.25a.75.75 0 0 1 1.06 0l3.074 3.073a20.923 20.923 0 0 1 5.545-4.931l-3.042-.815a.75.75 0 0 1-.53-.919Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            {trendingNames.map((name) => (
              <div
                key={name}
                className="bg-white/70 dark:bg-white/10 px-2 py-1 rounded-md text-sm font-ubuntu text-[#222]/70 dark:text-white/70 cursor-pointer"
                onClick={() => searchForName(name)}
              >
                {name}
              </div>
            ))}
          </div>

        </div>
      ) : (
        <GridTimeline
          customDates={timelineData.events}
          personName={timelineData.personName}
          scale={scale}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          onReset={() => setTimelineData(null)}
        />
      )}
    </div>
  );
}
